import type { AppShellBanner } from "../../../../../../shared/frontend/layout/AppShell";
import { useEffect, useState } from "react";

import { useAuth } from "../auth/useAuth";

const MAX_PENDING_LOAD_ATTEMPTS = 6;
const PENDING_RETRY_DELAY_MS = 5000;
const ENVIRONMENT_PRODUCTION = "production";
const OUTBOUND_MODE_LIVE = "live";

type EnvironmentInfo = {
  /** MqEnvironment des Backends: production oder dev. */
  environment: typeof ENVIRONMENT_PRODUCTION | "dev";
  /** OutboundMode des Backends, kleingeschrieben. */
  outboundMode: typeof OUTBOUND_MODE_LIVE | "blocked";
  pending?: boolean;
};

// Permanentes Banner in DEV (auch wenn das Gate live ist) und im Sicherheitsmodus
// auf production (Stempel-Mismatch nach DB-Kopie). In DEV bleibt bewusster Versand an;
// nur die automatische Kommunikation ist aus.
// Solange der Stempel-Check beim Start noch läuft (pending), erscheint kein
// Banner — kurz erneut nachsehen statt einen vorläufigen Zustand festzunageln.
export function useEnvironmentBanner(tr: (key: string) => string): AppShellBanner | undefined {
  const { api } = useAuth();
  const [info, setInfo] = useState<EnvironmentInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const load = async (attempt: number) => {
      try {
        const data = await api.get<EnvironmentInfo>("/api/v1/system/environment", { suppressGlobalErrorToast: true });
        if (cancelled) return;
        // Nach ~30 s trotzdem anzeigen — ein dauerhaft hängender Check darf
        // einen blockierten Zustand nicht unsichtbar machen.
        if (data.pending && attempt < MAX_PENDING_LOAD_ATTEMPTS) {
          timer = window.setTimeout(() => void load(attempt + 1), PENDING_RETRY_DELAY_MS);
          return;
        }
        setInfo(data);
      } catch {
        // Ohne Antwort kein Banner — der Status ist rein informativ.
      }
    };
    void load(0);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [api]);

  if (!info) return undefined;
  const isProduction = info.environment === ENVIRONMENT_PRODUCTION;
  if (isProduction && info.outboundMode === OUTBOUND_MODE_LIVE) return undefined;

  if (isProduction) {
    return (
      ({type: "error",
message: tr("Safe mode — external sending blocked")})
    );
  }

  return (
    ({type: "warning",
message: `${tr("Test environment — external sending disabled")} (${info.environment})`})
  );
}
